import axios, { AxiosResponse, AxiosError } from 'axios';
import { dispatchTrack } from './tracking';
import { TRACKING_OPERATION, TRACKING_PAGE } from './tracking/constants';
import { EventInfo } from './tracking/types';
import { reportAPI as reportAPIImplement } from '@/services/report';
import { getChatbotApiQuery } from '@/services/request';
import dateLib from '@/utils/dateLib/moment';
import { isBank } from '@/config/core';
import firstInitStore from '@/stores/firstInit';

export type EventUsageId = 0 | 1;

export enum API_TYPE {
  SUCCESS = 1,
  LOGIC_ERROR,
  NETWORK_ERROR,
}

export const REPORT_LABEL = {
  HTTP_ERROR_4XX: 'http_error_4xx',
  HTTP_ERROR_5XX: 'http_error_5xx',
  CODE_NOT_EQUAL_TO_ZERO: 'code_not_equal_to_zero',
  NETWORK_ERROR: 'network_error',
  SUCCESS: 'success',
};

const PREFIX = `${+new Date()} 【FE REPORT】`;

export const reportPageName = ENVIRONMENT === 'live' ? 'chatbot_deploy_home' : 'test_chatbot_deploy_home';
export interface Counter {
  count?: string | number;
  label: string;
  extend_1?: number | string;
  extend_2?: number | string;
}
export interface TimeCost {
  cost?: string | number;
  label: string;
}

const NEED_FILTER_PATHS = [
  '/report/v1/report_log', // 上报log接口
  '/heartbeat', // chat检测冲突心跳接口
];

// 2023.3.9 bank暂时不report日志
const reportAPI = isBank() ? null : reportAPIImplement;
window.ShopeeChatbot.report = isBank() ? () => {} : window.ShopeeChatbot.report;

window.ShopeeChatbot.reportLog = (level: 'error' | 'warn' | 'debug', msg: string, data?: object) => {
  reportAPI
    ?.post('/report_log', {
      msg: `[APP] ${PREFIX} ${msg} &time=${dateLib.format(new Date(), 'YYYY MMM DD hh:ii:ss')}&timestamp=${Date.now()}`,
      values: {
        level,
        UA: navigator.userAgent,
        location: window.location.href,
        ...data,
      },
    })
    .catch((err) => {
      console.error('Reporter::apiLog', err);
    });
};

if (!window?.ShopeeChatbot?.report) {
  if (ENVIRONMENT !== 'live') {
    throw new Error('ShopeeChatbot Report Utilities Loaded Failed.');
  }
  console.warn('ShopeeChatbot Report Utilities Loaded Failed.');
}

type Meta = {
  [propsName: string]: string | number;
};

class Reporter {
  meta: Meta;

  constructor() {
    this.meta = {
      UA: navigator.userAgent,
      location: location.href,
    };
  }

  updateMeta(meta: Meta = {}) {
    return [
      {
        name: 'meta',
        value: JSON.stringify({
          ...this.meta,
          ...meta,
        }),
      },
    ];
  }

  updateMatrix(matrix: TimeCost | Counter) {
    return { ...matrix };
  }

  async apiLog(
    msg: string,
    values: {
      level?: 'error' | 'warn' | 'debug';
    } & { [propsName: string]: string | boolean } = { level: 'error' },
  ) {
    await firstInitStore.initPromise;

    return reportAPI
      ?.post('/report_log', {
        msg: `[APP] ${PREFIX} ${msg} &time=${dateLib.format(new Date(), 'YYYY MMM DD hh:ii:ss')}&timestamp=${Date.now()}`,
        values: {
          ...values,
          ...this.meta,
        },
      })
      .catch((err) => {
        console.error('Reporter::apiLog', err);
      });
  }

  async apiLogBeforeUnload(
    msg: string,
    values: {
      level?: 'error' | 'warn' | 'debug';
    } & { [propsName: string]: string } = { level: 'error' },
  ) {
    const headers = { type: 'application/json' };
    const blob = new Blob([JSON.stringify({
      msg: `[APP] ${PREFIX} ${msg} &time=${dateLib.format(new Date(), 'YYYY MMM DD hh:ii:ss')}&timestamp=${Date.now()}`,
      values: {
        ...values,
        ...this.meta,
      },
    })], headers);

    try {
      navigator.sendBeacon(
        `report/v1/report_log?${getChatbotApiQuery()}`, blob);
    } catch (err) {
    }
  }

  apiReport({
    count = [],
    time_costs = [],
  }: {
    count?: Counter[];
    time_costs?: TimeCost[];
  } = {}) {
    window?.ShopeeChatbot?.report({
      count,
      time_costs,
    });
  }

  async apiBatchReport(data: {
    type: string;
    value: { label: string; count?: string | number; cost?: string | number; value?: string | number, extend_1?: number | string, extend_2?: number | string };
  }) {
    await firstInitStore.initPromise;

    window?.ShopeeChatbot?.putInReportQueue(data);
  }

  biReport(info: EventInfo) {
    const { page_type, operation, data } = info;
    dispatchTrack({
      operation: operation,
      page: page_type,
      usage_id: 1,
      data,
    });
  }
}

const reporter = new Reporter();

const generateReportUrl = (baseURL = '', url = '') => {
  const httpReg = /^http(s)?:\/\//;
  if (httpReg.test(url)) {
    return url;
  } else {
    const formatBaseURL = baseURL.endsWith('/') ? baseURL : `${baseURL}/`;
    const formatURL = url.startsWith('/') ? url.slice(1) : url;
    return `${httpReg.test(baseURL) ? '' : window?.location?.origin}${formatBaseURL}${formatURL}`;
  }
};

export const apiInterceptors = (res: AxiosResponse | AxiosError) => {
  try {
    if (axios?.isCancel?.(res)) {
      return;
    }
    const { status, data } = res as AxiosResponse || {};
    const { code, config, response: errResponse, isAxiosError = false } = res as AxiosError || {};
    const response = isAxiosError ? errResponse : res;
    const { baseURL = '', url = '' } = config;
    const reportURL = generateReportUrl(baseURL, url);
    const reportCode = typeof status !== 'undefined' ? String(status) : (typeof errResponse?.status !== 'undefined' ? String(errResponse?.status) : String(code || 'unknown'));
    const isFilterAPI = NEED_FILTER_PATHS.some((filterPath) => reportURL.indexOf(filterPath) !== -1);
    if (isFilterAPI) {
      return;
    }
    if (isAxiosError) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      reporter.apiLog('network error', { level: 'error', ...(response || res?.toJSON?.() || {}) });

    }
    const [requestPerformance] = performance?.getEntriesByName?.(response?.request?.responseURL) || [];
    if (requestPerformance instanceof PerformanceResourceTiming) {
      const dns = Math.round(requestPerformance.domainLookupEnd - requestPerformance.domainLookupStart);
      const tcp = Math.round(requestPerformance.connectEnd - requestPerformance.connectStart);

      let apiType = API_TYPE.SUCCESS;
      // 不是所有请求都有 data.code, 比如 /static/translation/
      if (data?.code && Number(data.code) !== 0) {
        apiType = API_TYPE.LOGIC_ERROR;
      }
      if (status as number >= 400) {
        apiType = API_TYPE.NETWORK_ERROR;
      }

      const api_url = requestPerformance.name;
      const api_code = data?.code?.toString() || 'unknown';
      const net_code = status;
      const api_type = apiType.toString();
      const api_time = Math.round(requestPerformance.duration);

      const requestTime = Math.round(requestPerformance.responseStart - requestPerformance.requestStart);
      const responseTime = Math.round(requestPerformance.responseEnd - requestPerformance.responseStart);
      const stalled = Math.round(requestPerformance.requestStart - requestPerformance.connectStart);
      const network = 'unknown';

      reporter.biReport({
        page_type: TRACKING_PAGE.GAME_HIPO_TRACK,
        operation: TRACKING_OPERATION.ACTION_HIPO,
        data: {
          page_name: reportPageName,
          hipo_type: 'action_api_request',
          json_str: JSON.stringify({
            api_url,
            api_code,
            net_code,
            api_type,
            api_time,
            dns,
            tcp,
            request: requestTime,
            response: responseTime,
            stalled,
            network,
          }),
        },
      });
    }
    let cost: number;
    if (requestPerformance?.duration) {
      cost = Math.round(requestPerformance?.duration);
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const start = config?.requestStartTime;
      const end = Date.now();
      cost = Math.round(end - start);
    }
    reporter.apiBatchReport({
      type: 'time',
      value: {
        label: 'channel_chat_api_time_consuming',
        cost, // api cost
        extend_1: reportURL, // url
        extend_2: reportCode, // http response code
      },
    });
  } catch (error) {
    console.error(error);
  }
};

export default reporter;
